import { BehaviorSubject } from 'rxjs';
import { addError, clearError } from './Error';
import {
  getAttributions,
  listAttributionsByTallyRun,
  listAssetRevenueAttributions
} from '../Api/Attribution/AttributionApi';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';

export interface Attribution {
  id: string;
  tallyRunId: number;
  runDate: string;
  runStatus: string;
  chargeRecurringExpected?: number;
  chargeRecurringActual?: number;
  chargeRecurringDifference?: number;
  revenueRecurringExpected?: number;
  revenueRecurringActual?: number;
  revenueRecurringDifference?: number;
  revenueRecurringPercentExpected?: number;
  revenueRecurringPercentActual?: number;
  chargeOnetimeExpected?: number;
  chargeOnetimeActual?: number;
  chargeOnetimeDifference?: number;
  revenueOnetimeExpected?: number;
  revenueOnetimeActual?: number;
  revenueOnetimeDifference?: number;
  revenueTrueupExpected?: number;
  revenueTrueupActual?: number;
  revenueSpiffActual?: number;
  paymentFrequency: string;
  allocationCount: number;
  openTicket: boolean;
  revenueRecurringNetActual?: number;
  revenueOneTimeNetActual?: number;
  allocationMethods: string[];
  assetId?: string;
  assetType?: 'asset' | 'provisional_asset';
  assetName?: string;
  assetNumber?: string;
  assetSfId?: string | null;
  directSellerName?: string;
  updatedAt?: string;
}

export interface ResponseAttributionRecord {
  entityType: string;
  entityId: string;
  attributions: Attribution[];
}

const attributionSubject = new BehaviorSubject<ResponseAttributionRecord | null>(null);
const attributionLoadingSubject = new BehaviorSubject<boolean>(false);

export const getAttributionSubject = (): BehaviorSubject<ResponseAttributionRecord | null> => attributionSubject;
export const getAttributionLoadingSubject = (): BehaviorSubject<boolean> => attributionLoadingSubject;

export const fetchAttributions = async (entityType: string, entityId: string, forceRefresh = false) => {
  const currentValue = attributionSubject.getValue();
  if (
    (!forceRefresh && currentValue && currentValue.entityId === entityId && currentValue.entityType === entityType) ||
    (forceRefresh && currentValue?.entityId !== entityId && currentValue?.entityType !== entityType)
  ) {
    return;
  }
  attributionLoadingSubject.next(true);

  const { data, error } = await getAttributions(entityType, entityId);

  if (data) {
    clearError('attribution');
    attributionSubject.next(data);
  }
  if (error) addError('attribution', error.message);

  attributionLoadingSubject.next(false);
};

// Paginated attributions for the Attribution grid page
const inboundPaginatedFetchNumberSubject = new BehaviorSubject<number>(0);
const paginatedAttributionsSubject = new BehaviorSubject<PaginatedResponse<Attribution>>(
  {} as PaginatedResponse<Attribution>
);
const paginatedAttributionsLoadingSubject = new BehaviorSubject<boolean>(false);

export const getPaginatedAttributionsSubject = (): BehaviorSubject<PaginatedResponse<Attribution>> =>
  paginatedAttributionsSubject;
export const getPaginatedAttributionsLoadingSubject = (): BehaviorSubject<boolean> =>
  paginatedAttributionsLoadingSubject;

export const fetchPaginatedAttributions = async (
  tallyRunId: number,
  view: string,
  filters?: ApiAdvancedFilter,
  forceRefresh = false
): Promise<void> => {
  const newFetchNumber = inboundPaginatedFetchNumberSubject.getValue() + 1;
  inboundPaginatedFetchNumberSubject.next(newFetchNumber);
  if (paginatedAttributionsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  paginatedAttributionsLoadingSubject.next(true);
  const { data, error } = await listAttributionsByTallyRun(tallyRunId, view, filters);
  const currentFetchNumber = inboundPaginatedFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError('attribution');
    paginatedAttributionsSubject.next(data);
    paginatedAttributionsLoadingSubject.next(false);
  }
  if (error) addError('attribution', error.message);
};

export interface AssetRevenueAttribution {
  asset_upstack_id: string;
  run_date: string;
  customer: string | null;
  sales_agent: string | null;
  supplier: string | null;
  product: string | null;
  status: string | null;
  commissions_validation_sub_status: string | null;
  supplier_payment_type: string | null;
  supplier_invoiced: string | null;
  rpm_link_count: number;
  revenue_recurring_expected: number;
  revenue_recurring_actual: number;
  revenue_recurring_difference: number;
  revenue_recurring_percent_of_expected: number | null;
  revenue_recurring_correction: number;
  revenue_recurring_net_actual: number;
  charge_recurring_expected: number;
  charge_recurring_actual: number;
  charge_recurring_difference: number;
  revenue_onetime_expected: number;
  revenue_onetime_actual: number;
  revenue_onetime_difference: number;
  revenue_onetime_correction: number;
  revenue_onetime_net_actual: number;
  charge_onetime_expected: number;
  charge_onetime_actual: number;
  charge_onetime_difference: number;
  revenue_trueup_actual: number;
  revenue_spiff_actual: number;
  revenue_split: number;
  payment_frequency: string;
  open_ticket: string;
  allocation_methods: string;
}

const assetRevenueAttributionsSubject = new BehaviorSubject<PaginatedResponse<AssetRevenueAttribution>>(
  {} as PaginatedResponse<AssetRevenueAttribution>
);
const inboundAssetRevenueFetchNumberSubject = new BehaviorSubject<number>(0);

export const getAssetRevenueAttributionsSubject = (): BehaviorSubject<PaginatedResponse<AssetRevenueAttribution>> =>
  assetRevenueAttributionsSubject;

export const fetchAssetRevenueAttributions = async (
  tallyRunId: number,
  filters?: ApiAdvancedFilter,
  forceRefresh = false
): Promise<void> => {
  if (!forceRefresh && assetRevenueAttributionsSubject.getValue()?.data?.length > 0) return;

  // Bumped only past the dedupe check above, so a deduped call can't invalidate an in-flight fetch.
  const newFetchNumber = inboundAssetRevenueFetchNumberSubject.getValue() + 1;
  inboundAssetRevenueFetchNumberSubject.next(newFetchNumber);
  const { data, error } = await listAssetRevenueAttributions(tallyRunId, filters);
  if (newFetchNumber !== inboundAssetRevenueFetchNumberSubject.getValue()) return;

  if (data) {
    clearError('attribution');
    assetRevenueAttributionsSubject.next(data);
  }
  if (error) addError('attribution', error.message);
};
